import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft, HiArrowNarrowRight } from "react-icons/hi";
import QuestionForms from "src/components/user/question-forms-component";
import { gsap } from "gsap";

function HeroContainer() {
  const { i18n, t } = useTranslation();
  const [showQuestionForms, setShowQuestionForms] = useState<boolean>(false);
  const formQuestionRef = useRef<HTMLDivElement>(null);
  const circle1 = useRef(null);
  const circle2 = useRef(null);
  const circle3 = useRef(null);
  const circle4 = useRef(null);
  const circle5 = useRef(null);

  const circleMobile1 = useRef(null);
  const circleMobile2 = useRef(null);
  const circleMobile3 = useRef(null);

  // green circle animations
  useEffect(() => {
    gsap.to(circle1.current, {
      duration: 4,
      repeat: -1,
      x: -25,
      y: -8,
      ease: "none",
      yoyo: true,
      repeatDelay: 0.5,
    });
    gsap.to(circle2.current, {
      duration: 4,
      repeat: -1,
      x: -25,
      y: 10,
      ease: "none",
      yoyo: true,
      repeatDelay: 0.2,
    });
    gsap.fromTo(
      circle3.current,
      {
        duration: 4,
        repeat: -1,
        x: -10,
        y: -7,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      },
      {
        duration: 4,
        repeat: -1,
        x: 5,
        y: 4,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      }
    );
    gsap.to(circle4.current, {
      duration: 4,
      repeat: -1,
      x: 10,
      y: -20,
      ease: "none",
      yoyo: true,
      repeatDelay: 0.2,
    });
    gsap.fromTo(
      circle5.current,
      {
        duration: 4,
        repeat: -1,
        x: -5,
        y: -10,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      },
      {
        duration: 4,
        repeat: -1,
        x: -10,
        y: 10,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      }
    );
    gsap.to(circleMobile1.current, {
      duration: 4,
      repeat: -1,
      x: 15,
      y: 5,
      ease: "none",
      yoyo: true,
      repeatDelay: 0.2,
    });
    gsap.to(circleMobile2.current, {
      duration: 4,
      repeat: -1,
      x: 15,
      y: -25,
      ease: "none",
      yoyo: true,
      repeatDelay: 0.2,
    });
    gsap.fromTo(
      circleMobile3.current,
      {
        duration: 3,
        repeat: -1,
        x: 15,
        y: -10,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      },
      {
        duration: 3,
        repeat: -1,
        x: -15,
        y: -5,
        ease: "none",
        yoyo: true,
        repeatDelay: 0.2,
      }
    );
    const handleClickOutSideForm = ({ target }: MouseEvent) => {
      if (!formQuestionRef?.current?.contains(target as Node)) {
        setShowQuestionForms(false);
      }
    };
    document.body.addEventListener("click", handleClickOutSideForm, true);

    return () => {
      document.body.removeEventListener("click", handleClickOutSideForm);
    };
  }, []);

  return (
    <div>
      {showQuestionForms ? (
        <div className="absolute z-30" ref={formQuestionRef}>
          <QuestionForms></QuestionForms>
        </div>
      ) : (
        <></>
      )}
      <div className="h-[53vw] tablet:h-[45vw] mobile:h-[60vw]">
        <video
          loop
          muted
          autoPlay
          playsInline
          src="https://github.com/SmahBamomen/alefredo/assets/53178276/adfe3eb0-bead-4f27-920c-c736b7d72ecd"
          className="relative -z-2 top-0 right-0"
        />
      </div>

      <div className="mobile:hidden">
        {/* green circles under the white circle */}
        <div
          ref={circle5}
          className={`${
            i18n.language === "ar" ? "right-[33%]" : "left-[33%]"
          } w-[15%] rounded-full absolute top-[1vw] -z-1 aspect-square bg-mint-500`}
        ></div>
        <div
          className={`
           ${
             i18n.language === "ar"
               ? "left-[50%] rounded-full w-[68%]"
               : "left-0 rounded-tr-full rounded-br-full h-[73vw] w-[53%]"
           } bg-zinc-50 absolute z-0 -top-[5vw] aspect-square`}
        >
          {/* green circles */}
          <div
            ref={circle4}
            className={`${
              i18n.language === "ar" ? "right-[93%]" : "left-[93%]"
            } w-[8%] rounded-full absolute top-[20.5vw] z-1 aspect-square bg-mint-500`}
          ></div>
          <div
            ref={circle3}
            className={`${
              i18n.language === "ar" ? "right-[91%]" : "left-[91%]"
            } w-[8%] rounded-full absolute top-[45.5vw] z-1 aspect-square bg-mint-500`}
          ></div>
          <div
            ref={circle2}
            className={`${
              i18n.language === "ar" ? "right-[84%]" : "left-[84%]"
            } w-[4.5%] rounded-full absolute top-[44vw] z-1 aspect-square bg-mint-500`}
          ></div>
          <div
            ref={circle1}
            className={`${
              i18n.language === "ar" ? "right-[69.5%]" : "left-[69.5%]"
            } w-[20%] rounded-full absolute top-[47vw] z-1 aspect-square bg-mint-500`}
          ></div>
        </div>
        <div
          dir={i18n.language === "ar" ? "rtl" : "ltr"}
          className="flex flex-col z-1 absolute top-0"
        >
          <p
            className={`${
              i18n.language === "ar" ? "mr-12 w-[40%]" : "ml-12 w-[45%]"
            } font-arMedium text-5xl mt-[20vw] text-navey-800 laptop:text-[22px] tablet:text-xl`}
          >
            {t("hero.container.description")}
          </p>

          <button
            onClick={() => {
              setShowQuestionForms(true);
            }}
            className={`${
              i18n.language === "ar" ? "mr-12" : "ml-12"
            } group flex flex-row gap-2 justify-center items-center font-arMedium w-52 tablet:w-40 tablet:h-12 h-16 mt-3 rounded-xl border-navey-600 text-navey-500 border-2 text-2xl tablet:text-base hover:w-60 tablet:hover:w-44 hover:bg-navey-700 hover:border-navey-700 hover:text-white transition-all ease-in-out delay-250 duration-300`}
          >
            {t("hero.container.discoverSchools")}
            {i18n.language === "ar" ? (
              <HiArrowNarrowLeft className="text-tart-500 hidden group-hover:inline-block" />
            ) : (
              <HiArrowNarrowRight className="text-tart-500 hidden group-hover:inline-block" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile  */}
      <div className="tablet:hidden laptop:hidden largeLaptop:hidden desktop:hidden largeDesktop:hidden">
        {/* Mobile green circles */}
        <div
          ref={circleMobile1}
          className="w-[16%] rounded-full absolute right-[82%] top-[32vw] z-10 aspect-square bg-mint-500"
        ></div>
        <div
          ref={circleMobile2}
          className="w-[23%] rounded-full absolute left-[73%] top-[33vw] z-0 aspect-square bg-mint-500"
        ></div>
        <div
          ref={circleMobile3}
          className="w-[7.5%] rounded-full absolute right-[73%] top-[31vw] z-10 aspect-square bg-mint-500"
        ></div>
        <div className="w-[100%] h-[55vw] bg-zinc-50 rounded-[50%] absolute z-0 top-[30vw]"></div>
        <div className="flex flex-col w-full justify-center z-1 absolute top-0 right-0">
          <p className="font-ar text-center text-lg mx-auto w-[calc(100vw-80px)] mt-[50vw] text-navey-800 tablet:text-2xl">
            {t("hero.container.description")}
          </p>
          <button
            onClick={() => {
              setShowQuestionForms(true);
            }}
            className="font-arMedium w-40 h-11 mx-auto mt-3 rounded-xl border-navey-500 text-navey-500 border-2 text-base"
          >
            {t("hero.container.discoverSchools")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HeroContainer;
